// Which cached queries stop being true when the backend's data changes, and how
// they get marked stale without anyone asking.
//
// Imports, suppliers and emissions all feed a set of DERIVED views — Home's two
// diagnostic panels, the analytics figures behind them, the report list — that
// live under their own query keys and have no idea a write happened. Left alone,
// adding an import leaves Home showing the pre-import world until `staleTime`
// lapses or the tab is refocused.
//
// This used to be a helper each mutation called by hand. That put the knowledge
// in the wrong place twice over: every new hook had to remember, and the ones
// that DON'T go through react-query — the ~200 raw `http.post/put/patch/remove`
// calls, against ~120 `useMutation` sites — had no hook to remember it in. Three
// call sites had already been pushed up into component callbacks to compensate.
//
// So the trigger moved down to the only door every write shares: the axios
// response interceptor in `requests.js`. A 2xx on a non-GET IS the fact that
// makes these views untrue, so that is where they are marked. Nothing in a
// feature module participates.
//
// React Query matches query keys by PREFIX, so `['analytics']` covers
// `['analytics', 'declarant', year, quarter, …]` and every cross-period column
// Home fans out, without this file needing to know their shape.

/** Derived views of declarant data, by query-key prefix. */
export const DECLARANT_DERIVED_QUERY_KEYS = [
  // Home's period summary: import counts, reporting state, whether the period
  // already has a report.
  ['declarantOverview'],
  // Per-supplier emissions, certificates and costs — the figures both Home
  // panels rank and price on, and the whole cross-period matrix.
  ['analytics'],
  // The account-wide supplier gap rows behind the Data gaps buckets.
  ['homeSupplierGapRows'],
  // The ambiguous-import blocker on Next steps.
  ['homeAmbiguousImports'],
  // "Filed / amendable" per year, and the Reports list itself.
  ['reports'],
  // The gap counters on the suppliers list, and the chase table's rows.
  ['supplierGapStats'],
  ['supplierGapChaseData'],
  // ── The UK CBAM bill ──────────────────────────────────────────────────
  // Every figure on the costs page is DERIVED from imports, suppliers,
  // installations and emission factors — the server reprices each line from
  // whatever those hold at the moment it is asked. So an import edited on
  // another page makes all three of these untrue, and none of them had any way
  // to find out: they carry a five-minute `staleTime`, so even navigating back
  // to the page served the pre-edit bill from cache.
  //
  // The three are listed together because they are three answers to one
  // question, and a page showing two of them refreshed is a page that
  // disagrees with itself. They must stay in step with `UK_COSTS_QUERY_ROOTS`
  // in `modules/UkCosts/hooks/useSaveUkAssumptions.js`, which the costs
  // module's own saves invalidate; `autoInvalidate.test.js` asserts the two
  // lists match. They are spelled out HERE rather than imported from there,
  // because this module is loaded by `requests.js` and importing a feature
  // hook — which imports `requests.js` — would close a cycle.
  ['uk-cbam-costs-rates'],
  ['uk-cbam-costs-lines'],
  ['uk-cbam-costs-summary'],
  // The UK CBAM report is the same bill, folded (uk-cbam-client-report CR1).
  ['uk-cbam-report'],
];

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// POST is NOT a reliable write signal here: this API answers most list/search
// queries with POST so the filters can travel in a body. `/suppliers/search`,
// `/shipments/search`, `/emissions/search/data`, `/suppliers/gap-stats` and a
// dozen more are READS issued from a react-query `queryFn`.
//
// Treating them as writes caused a self-sustaining refetch loop: four of the
// seven derived keys above are backed by POST endpoints, so fetching one
// invalidated it, which refetched it, which invalidated it again — every
// COALESCE_MS, forever, until Clerk started answering the token mints behind
// each request with 429 `too_many_requests` and Home's panels fell back to
// their error states.
const READ_POST_PATTERNS = [
  /\/search(\/|$|\?)/, // /suppliers/search, /emissions/search/data
  /\/gap-stats(\/|$|\?)/, // /suppliers/gap-stats
  /\/emissions\/search\/shipment(\/|$|\?)/, // the priced cost search (EU and UK), filtered by body
];

// The endpoints that BACK the derived queries. A response from one of these can
// never be allowed to schedule an invalidation, whatever its method: that is
// the query invalidating itself, which is the loop. Kept separate from the
// pattern list above because this one is a structural invariant rather than a
// convenience — `autoInvalidate.test.js` asserts every entry is classified as a
// read, so adding a derived key backed by a POST endpoint fails there rather
// than in production.
export const DERIVED_SOURCE_PATTERNS = [
  /\/suppliers\/search(\/|$|\?)/, // homeSupplierGapRows, supplierGapChaseData
  /\/shipments\/search(\/|$|\?)/, // homeAmbiguousImports
  /\/suppliers\/gap-stats(\/|$|\?)/, // supplierGapStats
  // The priced cost search — the EU bill, and since backend unify-uk-eu-cost-endpoint
  // S6/S7 the UK bill too (`uk-cbam-costs-lines` / `-summary` query roots). NARROW ON
  // PURPOSE — `POST /emissions` and `/uk-cbam-costs/rates/import` / `/relief` are real
  // writes, and a pattern that swallowed them would stop a save refreshing anything.
  /\/emissions\/search\/shipment(\/|$|\?)/,
];

const matchesAny = (patterns, path) => patterns.some((re) => re.test(path));

/**
 * Did this response change server state, such that the derived views above are
 * now untrue?
 *
 * A FAILED write changed nothing, so it must not schedule a refetch that would
 * return the same bytes. Setting `skipInvalidation` on the request config opts a
 * hot path out — deliberately opt-OUT: forgetting it costs one refetch, whereas
 * the old opt-IN cost a wrong number on screen.
 *
 * @param {import('axios').AxiosResponse} response
 * @param {string} apiUrl base URL of our backend — third-party calls on the
 *   shared global axios instance must not trigger anything.
 */
export const isStateChangingResponse = (response, apiUrl) => {
  const config = response?.config;
  if (!config || config.skipInvalidation) return false;
  const method = String(config.method || '').toLowerCase();
  if (!WRITE_METHODS.has(method)) return false;

  const path = String(config.url || '');
  // Never from a derived view's own source, whatever the method.
  if (matchesAny(DERIVED_SOURCE_PATTERNS, path)) return false;
  // POST-as-query is a read.
  if (method === 'post' && matchesAny(READ_POST_PATTERNS, path)) return false;
  if (!apiUrl || !String(config.url || '').startsWith(apiUrl)) return false;
  return response.status >= 200 && response.status < 300;
};

// A trailing window, not a leading one — the last write in a burst is the one
// whose result we want reflected. GraphFlow autosaves through raw `http.put`, so
// a node drag can emit several writes in a few hundred ms; without this each one
// would fan out its own pass.
export const COALESCE_MS = 250;

let timer = null;

/**
 * Mark every derived view stale, now.
 *
 * Active queries refetch immediately; the rest are flagged and refetch when
 * their panel next mounts. A prefix with nothing cached costs nothing, which is
 * why this is safe to fire on an operator's writes too — their session has no
 * declarant entries to match.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const flushDerivedInvalidation = (queryClient) => {
  if (!queryClient) return;
  DECLARANT_DERIVED_QUERY_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

/**
 * Queue a derived-view invalidation, coalescing anything else queued within
 * `COALESCE_MS` into the same pass.
 *
 * Deliberately not awaitable — the write's own list is what the user is looking
 * at, and the derived views should not hold its success toast up.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const scheduleDerivedInvalidation = (queryClient) => {
  if (!queryClient) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    flushDerivedInvalidation(queryClient);
  }, COALESCE_MS);
};

/** Drop a pending pass. Tests only — the timer is module state. */
export const __resetForTests = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

export default scheduleDerivedInvalidation;
